import React, { useEffect, useState } from 'react';
import { Menu, Bell, Settings, Activity, Shield } from 'lucide-react';
import { useCachedManualCheck } from '@/hooks/useApi';
import { useRegisterRefresh } from '@/contexts/RefreshContext';
import { healthApi, agentsApi, simulatorsApi } from '@/services/api';
import { cn, formatters } from '@/utils';

interface HeaderProps {
  onMenuClick: () => void;
  title: string;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick, title }) => {
  // Direct API state for header data
  const [headerData, setHeaderData] = useState<{
    simulatorsHealth: any;
    agentsHealth: any;
    loading: boolean;
  }>({
    simulatorsHealth: null,
    agentsHealth: null,
    loading: true
  });

  // Fallback hooks for compatibility
  const { data: healthData, refresh: refreshHealth } = useCachedManualCheck(() => healthApi.check(), 'header-health');
  const { data: agentsHealth, refresh: refreshAgents } = useCachedManualCheck(() => agentsApi.getHealth(), 'header-agents');
  const { data: simulatorsHealth, refresh: refreshSimulators } = useCachedManualCheck(() => simulatorsApi.getHealth(), 'header-simulators');

  // Register refresh handlers with global system
  useRegisterRefresh('header-health', refreshHealth);
  useRegisterRefresh('header-agents', refreshAgents);
  useRegisterRefresh('header-simulators', refreshSimulators);

  // Direct API loading for header data
  useEffect(() => {
    const loadHeaderData = async () => {
      try {
        const [simulators, agents] = await Promise.allSettled([
          simulatorsApi.getHealth(),
          agentsApi.getMetrics()
        ]);
        
        setHeaderData({
          simulatorsHealth: simulators.status === 'fulfilled' ? simulators.value : null,
          agentsHealth: agents.status === 'fulfilled' ? agents.value : null,
          loading: false
        });
        
      } catch (error) {
        console.error('Header data loading error:', error);
        setHeaderData(prev => ({ ...prev, loading: false }));
      }
    };
    
    loadHeaderData();
    
    // Also load hook data as fallback
    refreshHealth();
    refreshAgents();
    refreshSimulators();
  }, []);

  // Manual refresh all health data with direct API calls
  const refreshAllHealth = async () => {
    setHeaderData(prev => ({ ...prev, loading: true }));
    
    try {
      const [simulators, agents] = await Promise.allSettled([
        simulatorsApi.getHealth(),
        agentsApi.getMetrics()
      ]);
      
      setHeaderData({
        simulatorsHealth: simulators.status === 'fulfilled' ? simulators.value : null,
        agentsHealth: agents.status === 'fulfilled' ? agents.value : null,
        loading: false
      });
      
      // Also refresh hooks as backup
      refreshHealth();
      refreshAgents();
      refreshSimulators();
    } catch (error) {
      console.error('Header refresh error:', error);
      setHeaderData(prev => ({ ...prev, loading: false }));
    }
  };

  const getSystemHealthStats = () => {
    const finalSimulatorsHealth = headerData.simulatorsHealth || simulatorsHealth;
    
    if (!finalSimulatorsHealth) {
      return { total: 0, healthy: 0 };
    }

    const systems = ['gkpos', 'sap_ecc', 'hybris'];
    const total = systems.length;
    const healthy = systems.filter(system => 
      finalSimulatorsHealth[system]?.is_healthy
    ).length;

    return { total, healthy };
  };

  const getAgentHealthStats = () => {
    const finalAgentsHealth = headerData.agentsHealth || agentsHealth;
    
    if (!finalAgentsHealth?.agent_metrics) {
      return { total: 0, healthy: 0 };
    }

    const agents = Object.values(finalAgentsHealth.agent_metrics);
    const total = agents.length;
    const healthy = agents.filter((agent: any) => agent.status === 'idle' || agent.status === 'active').length;

    return { total, healthy };
  };

  const systemStats = getSystemHealthStats();
  const agentStats = getAgentHealthStats();
  const finalAgentsHealth = headerData.agentsHealth || agentsHealth;
  const activeWorkflows = finalAgentsHealth?.orchestrator_metrics?.active_workflows_count || finalAgentsHealth?.active_workflows || 0;

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="flex items-center justify-between px-6 py-4">
        {/* Left side */}
        <div className="flex items-center space-x-4">
          {/* Mobile menu button */}
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors"
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Title */}
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
            <p className="text-sm text-gray-500">
              Last updated: {formatters.time(new Date())}
            </p>
          </div>
        </div>

        {/* Right side - Status indicators */}
        <div className="flex items-center space-x-6">
          {/* System Status */}
          <div className="flex items-center space-x-2">
            <Shield className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-600">Systems:</span>
            <span className={cn(
              'status-indicator text-xs font-medium',
              systemStats.healthy === systemStats.total ? 'status-healthy' : 'status-warning'
            )}>
              {systemStats.healthy}/{systemStats.total} Online
            </span>
          </div>

          {/* Agent Status */}
          <div className="flex items-center space-x-2">
            <Activity className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-600">Agents:</span>
            <span className={cn(
              'status-indicator text-xs font-medium',
              agentStats.healthy === agentStats.total ? 'status-healthy' : 'status-warning'
            )}>
              {agentStats.healthy}/{agentStats.total} Ready
            </span>
          </div>

          {/* Active Workflows */}
          {activeWorkflows > 0 && (
            <div className="flex items-center space-x-2">
              <div className="relative">
                <div className="absolute -top-1 -right-1 h-3 w-3 bg-blue-500 rounded-full animate-pulse"></div>
                <Bell className="h-4 w-4 text-gray-500" />
              </div>
              <span className="text-sm text-gray-600">
                {activeWorkflows} active workflow{activeWorkflows !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          {/* Overall System Health */}
          <div className="flex items-center space-x-2 px-3 py-1 rounded-full bg-gray-100">
            <div className={cn(
              'w-2 h-2 rounded-full',
              (systemStats.healthy === systemStats.total && agentStats.healthy === agentStats.total) ? 'bg-green-500' : 'bg-yellow-500'
            )} />
            <span className="text-sm font-medium text-gray-700">
              {(systemStats.healthy === systemStats.total && agentStats.healthy === agentStats.total) ? 'System Healthy' : 'Monitoring'}
            </span>
          </div>

          {/* Manual Health Check button */}
          <button 
            onClick={refreshAllHealth}
            className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors"
            title="Refresh health status"
          >
            <Activity className="h-4 w-4" />
          </button>

          {/* Settings button */}
          <button className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors">
            <Settings className="h-5 w-5" />
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
